/**
 * Unified Shift Service
 * Combines current shifts and historical shifts with stored statuses
 */

import { supabase } from '@/integrations/supabase/client';
import { DayShift, Employee, TimeLog } from '@/types';
import { startOfDay, endOfDay, isAfter, isBefore, isSameDay } from 'date-fns';
import { ShiftStatus, ShiftStatusBusinessRules, DEFAULT_BUSINESS_RULES } from './shift-status-types';
import { createTieredShiftStatusService, TieredShiftStatusService } from './tiered-shift-status-service';

// Validation result interface
interface ValidationResult {
  isValid: boolean;
  errors: { message: string }[];
  sanitized?: { title?: string; notes?: string };
}

// Temporary placeholder function until unified-validation-service is implemented
const validateShift = (shift: any): ValidationResult => {
  const errors: { message: string }[] = [];

  // Basic validation - ensure required fields exist
  if (!shift?.startTime) {
    errors.push({ message: 'Start time is required' });
  }
  if (!shift?.endTime) {
    errors.push({ message: 'End time is required' });
  }
  if (shift?.startTime && shift?.endTime && shift.startTime >= shift.endTime) {
    errors.push({ message: 'End time must be after start time' });
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized: {
      title: shift?.title?.trim() || undefined,
      notes: shift?.notes?.trim() || undefined,
    }
  };
};

export interface UnifiedShiftData {
  id: string;
  business_id: string;
  start_time: string;
  end_time: string;
  title: string | null;
  notes: string | null;
  employee_id: string | null;
  breaks: any;
  status: ShiftStatus | null;
  calculated_at: string | null;
  created_at: string;
  updated_at: string;
  employees?: { id: string; name: string } | null;
  isHistorical: boolean;
  was_historically_edited?: boolean;
  historical_edit_count?: number;
  source?: string;
}

export interface UnifiedShiftQueryResult {
  shifts: DayShift[];
  totalCount: number;
  historicalCount: number;
  currentCount: number;
  performanceStats: {
    storedStatusCount: number;
    realTimeCalculatedCount: number;
    cacheHitRate: number;
  };
}

export interface ShiftCreateParams {
  businessId: string;
  startTime: Date;
  endTime: Date;
  title: string;
  employeeId?: string;
  notes?: string;
  calculateStatus?: boolean;
}

export interface ShiftUpdateParams {
  id: string;
  businessId?: string;
  startTime?: Date;
  endTime?: Date;
  title?: string;
  employeeId?: string | null;
  notes?: string;
  breaks?: any;
}

/**
 * Unified Shift Service class
 */
export class UnifiedShiftService {
  // No caching - removed all cache implementations

  /**
   * Load business rules and timezone for status calculation (no caching)
   */
  private static async getBusinessRulesAndTimezone(businessId: string): Promise<{ rules: ShiftStatusBusinessRules; timezone: string }> {
    try {
      const { data: business } = await supabase
        .from('businesses')
        .select(`
          timezone,
          late_arrival_threshold_minutes,
          grace_period_minutes,
          auto_missed_after_minutes,
          early_arrival_threshold_minutes,
          early_departure_threshold_minutes,
          enable_overtime_tracking,
          overtime_threshold_minutes,
          overtime_timeout_hours,
          require_arrival_confirmation
        `)
        .eq('id', businessId)
        .single();

      return {
        rules: this.createBusinessRulesFromRawData(business || {}),
        timezone: business?.timezone || 'UTC'
      };
    } catch (error) {
      console.warn(`Failed to load business rules for ${businessId}, using defaults:`, error);
      return { rules: DEFAULT_BUSINESS_RULES, timezone: 'UTC' };
    }
  }

  /**
   * @deprecated Use getBusinessRulesAndTimezone instead
   * Load business rules for status calculation (no caching)
   */
  private static async getBusinessRules(businessId: string): Promise<ShiftStatusBusinessRules> {
    const { rules } = await this.getBusinessRulesAndTimezone(businessId);
    return rules;
  }

  /**
   * Create business rules from raw database data
   */
  private static createBusinessRulesFromRawData(data: any): ShiftStatusBusinessRules {
    return {
      lateArrivalThresholdMinutes: data?.late_arrival_threshold_minutes ?? DEFAULT_BUSINESS_RULES.lateArrivalThresholdMinutes,
      gracePeriodMinutes: data?.grace_period_minutes ?? DEFAULT_BUSINESS_RULES.gracePeriodMinutes,
      autoMissedAfterMinutes: data?.auto_missed_after_minutes ?? DEFAULT_BUSINESS_RULES.autoMissedAfterMinutes,
      earlyArrivalThresholdMinutes: data?.early_arrival_threshold_minutes ?? DEFAULT_BUSINESS_RULES.earlyArrivalThresholdMinutes,
      earlyDepartureThresholdMinutes: data?.early_departure_threshold_minutes ?? DEFAULT_BUSINESS_RULES.earlyDepartureThresholdMinutes,
      enableOvertimeTracking: data?.enable_overtime_tracking ?? DEFAULT_BUSINESS_RULES.enableOvertimeTracking,
      overtimeThresholdMinutes: data?.overtime_threshold_minutes ?? DEFAULT_BUSINESS_RULES.overtimeThresholdMinutes,
      overtimeTimeoutHours: data?.overtime_timeout_hours ?? DEFAULT_BUSINESS_RULES.overtimeTimeoutHours,
      requireArrivalConfirmation: data?.require_arrival_confirmation ?? DEFAULT_BUSINESS_RULES.requireArrivalConfirmation,
    };
  }

  /**
   * Get tiered status service for a business (no caching)
   * Now properly passes business timezone for consistent status calculations
   */
  private static async getTieredStatusService(businessId: string): Promise<TieredShiftStatusService> {
    const { rules, timezone } = await this.getBusinessRulesAndTimezone(businessId);
    return createTieredShiftStatusService(rules, timezone);
  }

  /**
   * Determines which table to query based on the date
   * Past dates: shifts_history only
   * Today and future: shifts only
   * Date ranges spanning both: both tables (legacy behavior)
   */
  private static determineTableStrategy(selectedDate: Date, endDate?: Date): 'current' | 'historical' | 'both' {
    const today = startOfDay(new Date());
    const queryDate = startOfDay(selectedDate);

    // Single date queries
    if (!endDate) {
      if (isSameDay(queryDate, today) || isAfter(queryDate, today)) {
        return 'current';
      } else {
        return 'historical';
      }
    }

    // Date range queries - check if range spans multiple periods
    const queryEndDate = startOfDay(endDate);

    // If query starts after today -> future only, use current shifts
    if (isAfter(queryDate, today)) {
      return 'current';
    }

    // If query ends before today (strictly before, not same day) -> past only, use historical
    // Use isBefore instead of !isAfter to correctly handle same-day case
    if (isBefore(queryEndDate, today)) {
      return 'historical';
    }

    // If query includes today (end date is today or after)
    // Check if it also includes past dates
    if (isBefore(queryDate, today)) {
      return 'both'; // Spans past and present
    }

    // Query starts on or after today -> use current shifts only
    return 'current';
  }
  /**
   * Fetch shifts for a specific date from both current and historical tables (no caching)
   */
  static async fetchShiftsForDay(
    businessId: string,
    selectedDate: Date,
    employees: Employee[] = [],
    businessTimezone?: string,
    businessCountryCode?: string
  ): Promise<UnifiedShiftQueryResult> {
    return await this.fetchShiftsForDayUncached(businessId, selectedDate, employees, businessTimezone, businessCountryCode);
  }

  /**
   * Internal method to fetch shifts without caching
   */
  private static async fetchShiftsForDayUncached(
    businessId: string,
    selectedDate: Date,
    employees: Employee[] = [],
    businessTimezone?: string,
    businessCountryCode?: string
  ): Promise<UnifiedShiftQueryResult> {
    const startTime = startOfDay(selectedDate).toISOString();
    const endTime = endOfDay(selectedDate).toISOString();
    const strategy = this.determineTableStrategy(selectedDate);
    
    // Determine business timezone - use provided timezone or browser timezone (no country fallback)
    const resolvedTimezone = businessTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      const selectClause = `
        id,
        business_id,
        start_time,
        end_time,
        title,
        notes,
        employee_id,
        breaks,
        status,
        calculated_at,
        created_at,
        updated_at,
        source,
        was_historically_edited,
        historical_edit_count,
        employees:employee_id(id, name)
      `;

      let currentShifts: UnifiedShiftData[] = [];
      let historicalShifts: UnifiedShiftData[] = [];

      // Smart table routing - only query the table we need
      if (strategy === 'current') {
        // Only query current shifts table for today/future dates
        const { data, error } = await supabase
          .from('shifts')
          .select(selectClause)
          .eq('business_id', businessId)
          .gte('start_time', startTime)
          .lte('end_time', endTime)
          .order('start_time');

        if (error) throw error;
        currentShifts = (data || []).map(shift => ({ ...shift, isHistorical: false }));

      } else if (strategy === 'historical') {
        // Only query historical shifts table for past dates
        const { data, error } = await supabase
          .from('shifts_history')
          .select(selectClause)
          .eq('business_id', businessId)
          .gte('start_time', startTime)
          .lte('end_time', endTime)
          .order('start_time');

        if (error) throw error;
        historicalShifts = (data || []).map(shift => ({ ...shift, isHistorical: true }));

      } else {
        // Legacy behavior: query both tables (for edge cases)
        const [currentResult, historicalResult] = await Promise.all([
          supabase.from('shifts').select(selectClause)
            .eq('business_id', businessId)
            .gte('start_time', startTime)
            .lte('end_time', endTime)
            .order('start_time'),
          supabase.from('shifts_history').select(selectClause)
            .eq('business_id', businessId)
            .gte('start_time', startTime)
            .lte('end_time', endTime)
            .order('start_time')
        ]);

        if (currentResult.error) throw currentResult.error;
        if (historicalResult.error) throw historicalResult.error;

        currentShifts = (currentResult.data || []).map(shift => ({ ...shift, isHistorical: false }));
        historicalShifts = (historicalResult.data || []).map(shift => ({ ...shift, isHistorical: true }));
      }

      const allShifts = [...currentShifts, ...historicalShifts];

      // Get time logs for the date if we have employees to process
      let timeLogs: TimeLog[] = [];
      const employeeIds = [...new Set(allShifts.filter(s => s.employee_id).map(s => s.employee_id))];
      
      if (employeeIds.length > 0) {
        const { data: timeLogsData } = await supabase
          .from('time_logs')
          .select('employee_id, event_type, scanned_at')
          .eq('business_id', businessId)
          .in('employee_id', employeeIds)
          .gte('scanned_at', startTime)
          .lte('scanned_at', endTime);

        timeLogs = timeLogsData || [];
      }

      // Get tiered status service for this business
      const tieredService = await this.getTieredStatusService(businessId);

      // Transform to DayShift format and calculate statuses using tiered approach
      let storedStatusCount = 0;
      let realTimeCalculatedCount = 0;

      const transformedShifts: DayShift[] = await Promise.all(
        allShifts.map(async (shift) => {
          const employeeName = shift.employees?.name ||
                              employees.find(e => e.id === shift.employee_id)?.name ||
                              '';

          // Find relevant time logs for this shift
          const shiftTimeLogs = timeLogs.filter(log =>
            log.employee_id === shift.employee_id
          );

          const arrivalLog = shiftTimeLogs
            .filter(log => log.event_type === 'arrival')
            .sort((a, b) => new Date(a.scanned_at).getTime() - new Date(b.scanned_at).getTime())[0];

          const departureLog = shiftTimeLogs
            .filter(log => log.event_type === 'departure')
            .sort((a, b) => new Date(b.scanned_at).getTime() - new Date(a.scanned_at).getTime())[0];

          // Use hybrid status calculation for consistent results
          const statusResult = await tieredService.getShiftStatus(
            {
              id: shift.id,
              start_time: shift.start_time,
              end_time: shift.end_time,
              employee_id: shift.employee_id,
              arrival_time: arrivalLog?.scanned_at || null,
              departure_time: departureLog?.scanned_at || null,
              stored_status: shift.status as ShiftStatus,
              calculated_at: shift.calculated_at,
            },
            {
              businessId,
              useStoredStatus: true,
            }
          );

          const finalStatus = statusResult.status;

          // Update performance counters based on actual calculation
          if (shift.isHistorical && shift.status && statusResult.status === shift.status) {
            storedStatusCount++;
          } else {
            realTimeCalculatedCount++;
          }

          // Keep times as UTC Date objects - presentation layer handles timezone formatting
          const startTime = new Date(shift.start_time);
          const endTime = new Date(shift.end_time);

          return {
            id: shift.id,
            employeeId: shift.employee_id,
            employeeName,
            startTime,
            endTime,
            title: shift.title || '',
            notes: shift.notes || '',
            isNew: false,
            isEditing: false,
            isHistorical: shift.isHistorical,
            requestCount: 0, // This would need to be fetched separately if needed
            breaks: shift.breaks || null,
            calculatedStatus: finalStatus,
            storedStatus: shift.status as ShiftStatus,
            calculatedAt: shift.calculated_at,
            actualArrivalTime: arrivalLog?.scanned_at,
            actualDepartureTime: departureLog?.scanned_at,
            source: shift.source || 'manual',
            business_id: shift.business_id,
            wasHistoricallyEdited: shift.was_historically_edited || false,
            historicalEditCount: shift.historical_edit_count || 0,
          };
        })
      );

      // Sort by start time
      transformedShifts.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

      const totalCount = transformedShifts.length;
      const historicalCount = historicalShifts.length;
      const currentCount = currentShifts.length;
      const cacheHitRate = totalCount > 0 ? (storedStatusCount / totalCount) * 100 : 0;

      return {
        shifts: transformedShifts,
        totalCount,
        historicalCount,
        currentCount,
        performanceStats: {
          storedStatusCount,
          realTimeCalculatedCount,
          cacheHitRate: Math.round(cacheHitRate * 100) / 100
        }
      };

    } catch (error) {
      // Handle unified shift query error silently
      throw error;
    }
  }

  /**
   * Fetch shifts for a date range from both tables (no caching or deduplication)
   */
  static async fetchShiftsForDateRange(
    businessId: string,
    startDate: Date,
    endDate: Date,
    employees: Employee[] = [],
    hidePastDays: boolean = false
  ): Promise<UnifiedShiftQueryResult> {
    return await this.fetchShiftsForDateRangeUncached(businessId, startDate, endDate, employees, hidePastDays);
  }

  /**
   * Internal method to fetch shifts for date range without deduplication
   */
  private static async fetchShiftsForDateRangeUncached(
    businessId: string,
    startDate: Date,
    endDate: Date,
    employees: Employee[] = [],
    hidePastDays: boolean = false
  ): Promise<UnifiedShiftQueryResult> {
    const startTime = startDate.toISOString();
    const endTime = endDate.toISOString();
    // Override strategy if hidePastDays is true - only query current shifts
    const strategy = hidePastDays ? 'current' : this.determineTableStrategy(startDate, endDate);

    try {
      const selectClause = `
        id,
        business_id,
        start_time,
        end_time,
        title,
        notes,
        employee_id,
        breaks,
        status,
        calculated_at,
        created_at,
        updated_at,
        source,
        was_historically_edited,
        historical_edit_count,
        employees:employee_id(id, name)
      `;

      let currentShifts: UnifiedShiftData[] = [];
      let historicalShifts: UnifiedShiftData[] = [];

      // Smart table routing for date ranges
      if (strategy === 'current') {
        // Only query current shifts table for future date ranges
        const { data, error } = await supabase
          .from('shifts')
          .select(selectClause)
          .eq('business_id', businessId)
          .gte('start_time', startTime)
          .lte('start_time', endTime)
          .order('start_time');

        if (error) throw error;
        currentShifts = (data || []).map(shift => ({ ...shift, isHistorical: false }));

      } else if (strategy === 'historical') {
        // Only query historical shifts table for past date ranges
        const { data, error } = await supabase
          .from('shifts_history')
          .select(selectClause)
          .eq('business_id', businessId)
          .gte('start_time', startTime)
          .lte('start_time', endTime)
          .order('start_time');

        if (error) throw error;
        historicalShifts = (data || []).map(shift => ({ ...shift, isHistorical: true }));

      } else {
        // Query both tables for ranges spanning past and present
        const [currentResult, historicalResult] = await Promise.all([
          supabase.from('shifts').select(selectClause)
            .eq('business_id', businessId)
            .gte('start_time', startTime)
            .lte('start_time', endTime)
            .order('start_time'),
          supabase.from('shifts_history').select(selectClause)
            .eq('business_id', businessId)
            .gte('start_time', startTime)
            .lte('start_time', endTime)
            .order('start_time')
        ]);

        if (currentResult.error) throw currentResult.error;
        if (historicalResult.error) throw historicalResult.error;

        currentShifts = (currentResult.data || []).map(shift => ({ ...shift, isHistorical: false }));
        historicalShifts = (historicalResult.data || []).map(shift => ({ ...shift, isHistorical: true }));
      }

      const allShifts = [...currentShifts, ...historicalShifts];

      // Get tiered status service for this business
      const tieredService = await this.getTieredStatusService(businessId);

      // Fetch time_logs for TODAY's shifts only (hybrid approach for performance)
      const today = startOfDay(new Date());
      const todayEnd = endOfDay(new Date());

      // Filter shifts that are today
      const todayShiftEmployeeIds = allShifts
        .filter(s => {
          const shiftDate = startOfDay(new Date(s.start_time));
          return isSameDay(shiftDate, today) && s.employee_id;
        })
        .map(s => s.employee_id)
        .filter((id): id is string => id !== null && id !== undefined);

      const uniqueTodayEmployeeIds = [...new Set(todayShiftEmployeeIds)];

      // Fetch time logs only for today's shifts
      let todayTimeLogs: TimeLog[] = [];
      if (uniqueTodayEmployeeIds.length > 0) {
        const { data: timeLogsData } = await supabase
          .from('time_logs')
          .select('employee_id, event_type, scanned_at')
          .eq('business_id', businessId)
          .in('employee_id', uniqueTodayEmployeeIds)
          .gte('scanned_at', today.toISOString())
          .lte('scanned_at', todayEnd.toISOString());

        todayTimeLogs = timeLogsData || [];
      }

      // For date ranges, we'll use tiered status calculation with time logs for today only
      let storedStatusCount = 0;
      let realTimeCalculatedCount = 0;

      const transformedShifts: DayShift[] = await Promise.all(
        allShifts.map(async (shift) => {
          const employeeName = shift.employees?.name ||
                              employees.find(e => e.id === shift.employee_id)?.name ||
                              '';

          // Check if this shift is today - if so, use time logs for accurate status
          const shiftDate = startOfDay(new Date(shift.start_time));
          const isShiftToday = isSameDay(shiftDate, today);

          let arrivalTime: string | null = null;
          let departureTime: string | null = null;

          if (isShiftToday && shift.employee_id) {
            // Find time logs for this employee on today
            const employeeTimeLogs = todayTimeLogs.filter(
              log => log.employee_id === shift.employee_id
            );

            const arrivalLog = employeeTimeLogs
              .filter(log => log.event_type === 'arrival')
              .sort((a, b) => new Date(a.scanned_at).getTime() - new Date(b.scanned_at).getTime())[0];

            const departureLog = employeeTimeLogs
              .filter(log => log.event_type === 'departure')
              .sort((a, b) => new Date(b.scanned_at).getTime() - new Date(a.scanned_at).getTime())[0];

            arrivalTime = arrivalLog?.scanned_at || null;
            departureTime = departureLog?.scanned_at || null;
          }

          // Use hybrid status calculation with time logs for today's shifts
          const statusResult = await tieredService.getShiftStatus(
            {
              id: shift.id,
              start_time: shift.start_time,
              end_time: shift.end_time,
              employee_id: shift.employee_id,
              arrival_time: arrivalTime,
              departure_time: departureTime,
              stored_status: shift.status as ShiftStatus,
              calculated_at: shift.calculated_at,
            },
            {
              businessId,
              useStoredStatus: !isShiftToday, // Use stored status for past shifts, real-time for today
            }
          );

          const finalStatus = statusResult.status;

          // Update performance counters
          if (shift.isHistorical && shift.status && statusResult.status === shift.status) {
            storedStatusCount++;
          } else {
            realTimeCalculatedCount++;
          }

        return {
          id: shift.id,
          employeeId: shift.employee_id,
          employeeName,
          startTime: new Date(shift.start_time),
          endTime: new Date(shift.end_time),
          title: shift.title || '',
          notes: shift.notes || '',
          isNew: false,
          isEditing: false,
          isHistorical: shift.isHistorical,
          requestCount: 0,
          breaks: shift.breaks || null,
          calculatedStatus: finalStatus,
          storedStatus: shift.status as ShiftStatus,
          calculatedAt: shift.calculated_at,
          actualArrivalTime: arrivalTime,
          actualDepartureTime: departureTime,
          source: shift.source || 'manual',
          business_id: shift.business_id,
          wasHistoricallyEdited: shift.was_historically_edited || false,
          historicalEditCount: shift.historical_edit_count || 0,
        };
      })
    );

      // Sort by start time
      transformedShifts.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

      const totalCount = transformedShifts.length;
      const historicalCount = historicalShifts.length;
      const currentCount = currentShifts.length;
      const cacheHitRate = totalCount > 0 ? (storedStatusCount / totalCount) * 100 : 0;

      return {
        shifts: transformedShifts,
        totalCount,
        historicalCount,
        currentCount,
        performanceStats: {
          storedStatusCount,
          realTimeCalculatedCount,
          cacheHitRate: Math.round(cacheHitRate * 100) / 100
        }
      };

    } catch (error) {
      // Handle unified shift range query error silently
      throw error;
    }
  }

  /**
   * Get statistics about shift status usage
   */
  static async getShiftStatusStatistics(businessId: string): Promise<{
    totalHistoricalShifts: number;
    shiftsWithStoredStatus: number;
    statusDistribution: Record<string, number>;
  }> {
    try {
      // Get historical shift statistics
      const { data: statusStats, error } = await supabase
        .from('shifts_history')
        .select('status')
        .eq('business_id', businessId);

      if (error) throw error;

      const totalHistoricalShifts = statusStats?.length || 0;
      const shiftsWithStoredStatus = statusStats?.filter(s => s.status).length || 0;

      // Build status distribution
      const statusDistribution: Record<string, number> = {};
      statusStats?.forEach(shift => {
        if (shift.status) {
          statusDistribution[shift.status] = (statusDistribution[shift.status] || 0) + 1;
        }
      });

      return {
        totalHistoricalShifts,
        shiftsWithStoredStatus,
        statusDistribution
      };

    } catch (error) {
      // Handle shift status statistics error silently
      return {
        totalHistoricalShifts: 0,
        shiftsWithStoredStatus: 0,
        statusDistribution: {}
      };
    }
  }

  /**
   * Create a new shift
   */
  static async createShift(params: ShiftCreateParams): Promise<DayShift> {
    try {
      // Validate input data
      const validation = validateShift({
        startTime: params.startTime,
        endTime: params.endTime,
        title: params.title,
        employeeId: params.employeeId,
        notes: params.notes
      });

      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.map(e => e.message).join(', ')}`);
      }

      // Use sanitized values if available
      const sanitizedTitle = validation.sanitized?.title || params.title;
      const sanitizedNotes = validation.sanitized?.notes || params.notes;

      // Check if this shift is being created on a past date (historical)
      const today = startOfDay(new Date());
      const shiftDay = startOfDay(params.startTime);
      const isCreatedRetroactively = shiftDay < today;

      // Insert into the correct table based on date
      // Past shifts go to shifts_history, today/future go to shifts
      const tableName = isCreatedRetroactively ? 'shifts_history' : 'shifts';

      const { data, error } = await supabase
        .from(tableName)
        .insert({
          business_id: params.businessId,
          start_time: params.startTime.toISOString(),
          end_time: params.endTime.toISOString(),
          title: sanitizedTitle,
          employee_id: params.employeeId || null,
          notes: sanitizedNotes || null,
          breaks: [],
          // Mark as historically edited if created on a past date
          was_historically_edited: isCreatedRetroactively,
          historical_edit_count: isCreatedRetroactively ? 1 : 0,
        })
        .select('*, employees(id, name)')
        .single();

      if (error) throw error;

      // No cache to invalidate

      // Transform to DayShift format
      return {
        id: data.id,
        business_id: data.business_id,
        startTime: new Date(data.start_time),
        endTime: new Date(data.end_time),
        title: data.title || '',
        employeeId: data.employee_id,
        employeeName: data.employees?.name || '',
        notes: data.notes,
        breaks: data.breaks || [],
        status: null, // Will be calculated on demand
        actualArrivalTime: null,
        actualDepartureTime: null,
        source: data.source || 'manual',
        wasHistoricallyEdited: data.was_historically_edited || false,
        historicalEditCount: data.historical_edit_count || 0,
      };
    } catch (error) {
      // Handle shift creation error silently
      throw error;
    }
  }

  /**
   * Update an existing shift
   */
  static async updateShift(params: ShiftUpdateParams): Promise<DayShift> {
    try {
      const updateData: any = {};

      if (params.startTime) updateData.start_time = params.startTime.toISOString();
      if (params.endTime) updateData.end_time = params.endTime.toISOString();
      if (params.title !== undefined) updateData.title = params.title;
      if (params.employeeId !== undefined) updateData.employee_id = params.employeeId;
      if (params.notes !== undefined) updateData.notes = params.notes;
      if (params.breaks !== undefined) updateData.breaks = params.breaks;

      // Check if this is a historical shift update (times changed on a past shift)
      const today = startOfDay(new Date());
      let isHistoricalUpdate = false;

      if (params.startTime) {
        const newShiftDay = startOfDay(params.startTime);
        isHistoricalUpdate = newShiftDay < today;
      }

      // If updating a historical shift, mark it as historically edited
      if (isHistoricalUpdate) {
        updateData.was_historically_edited = true;
      }

      // First try to update in shifts table
      let { data, error } = await supabase
        .from('shifts')
        .update(updateData)
        .eq('id', params.id)
        .select('*, employees(id, name)')
        .maybeSingle();

      // If not found in shifts, try shifts_history table
      if (!data && !error) {
        const historyResult = await supabase
          .from('shifts_history')
          .update(updateData)
          .eq('id', params.id)
          .select('*, employees(id, name)')
          .maybeSingle();

        data = historyResult.data;
        error = historyResult.error;
      }

      if (error) throw error;
      if (!data) {
        throw new Error(`Shift with id ${params.id} not found in shifts or shifts_history table`);
      }

      // If this was a historical update, also increment the edit count
      if (isHistoricalUpdate) {
        await supabase.rpc('increment_historical_edit_count', { shift_id_param: params.id });
      }

      // Transform to DayShift format
      return {
        id: data.id,
        business_id: data.business_id,
        startTime: new Date(data.start_time),
        endTime: new Date(data.end_time),
        title: data.title || '',
        employeeId: data.employee_id,
        employeeName: data.employees?.name || '',
        notes: data.notes,
        breaks: data.breaks || [],
        status: null, // Will be calculated on demand
        actualArrivalTime: null,
        actualDepartureTime: null,
        source: data.source || 'manual',
        wasHistoricallyEdited: data.was_historically_edited || false,
        historicalEditCount: data.historical_edit_count || 0,
      };
    } catch (error) {
      // Handle shift update error silently
      throw error;
    }
  }

  /**
   * Delete a shift
   */
  static async deleteShift(shiftId: string): Promise<void> {
    try {
      // First get the shift to know which business to invalidate cache for
      const { data: shiftData, error: selectError } = await supabase
        .from('shifts')
        .select('business_id')
        .eq('id', shiftId)
        .single();

      if (selectError) throw selectError;

      // Delete the shift
      const { error } = await supabase
        .from('shifts')
        .delete()
        .eq('id', shiftId);

      if (error) throw error;

      // No cache to invalidate
    } catch (error) {
      // Handle shift deletion error silently
      throw error;
    }
  }

  /**
   * Assign an employee to a shift
   * Handles both current shifts (in 'shifts' table) and historical shifts (in 'shifts_history' table)
   */
  static async assignEmployeeToShift(
    shiftId: string,
    employeeId: string | null
  ): Promise<DayShift> {
    try {
      // First, try to update in the current shifts table
      let { data, error } = await supabase
        .from('shifts')
        .update({ employee_id: employeeId })
        .eq('id', shiftId)
        .select('*, employees(id, name)')
        .maybeSingle();

      // If not found in shifts table, try shifts_history table
      if (!data && !error) {
        const historyResult = await supabase
          .from('shifts_history')
          .update({ employee_id: employeeId })
          .eq('id', shiftId)
          .select('*, employees(id, name)')
          .maybeSingle();

        data = historyResult.data;
        error = historyResult.error;
      }

      if (error) throw error;
      if (!data) {
        throw new Error(`Shift with id ${shiftId} not found in shifts or shifts_history table`);
      }

      // Transform to DayShift format
      return {
        id: data.id,
        business_id: data.business_id,
        startTime: new Date(data.start_time),
        endTime: new Date(data.end_time),
        title: data.title || '',
        employeeId: data.employee_id,
        employeeName: data.employees?.name || '',
        notes: data.notes,
        breaks: data.breaks || [],
        status: null, // Will be calculated on demand
        actualArrivalTime: null,
        actualDepartureTime: null,
        source: data.source || 'manual',
      };
    } catch (error) {
      // Handle employee assignment error silently
      throw error;
    }
  }

  /**
   * Check for employee conflicts before assigning to shift
   */
  static async checkEmployeeConflicts(
    employeeId: string,
    startTime: Date,
    endTime: Date,
    excludeShiftId?: string
  ): Promise<{ hasConflicts: boolean; conflicts: any[] }> {
    try {
      let query = supabase
        .from('shifts')
        .select('id, start_time, end_time, title')
        .eq('employee_id', employeeId)
        .or(`start_time.lte.${endTime.toISOString()},end_time.gte.${startTime.toISOString()}`);

      if (excludeShiftId) {
        query = query.neq('id', excludeShiftId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return {
        hasConflicts: (data?.length || 0) > 0,
        conflicts: data || []
      };
    } catch (error) {
      // Handle employee conflicts check error silently
      return {
        hasConflicts: false,
        conflicts: []
      };
    }
  }
}

// Export singleton instance for convenience
export const unifiedShiftService = UnifiedShiftService;